import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Stack,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Payments as PaymentsIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { customerService } from '../utils/apiService';
import settingsService from '../services/settingsService';
import { useTranslation } from '../hooks/useTranslation';


const NewCustomer = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
    const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    customerType: 'B2B',
    email: '',
    phone: '',
    vatNumber: '',
    identificationType: '',
    identificationNo: '',
    address: {
      street: '',
      shortAddress: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'SA'
    },
    isActive: true,
    code: '',
    description: '',
    paymentTerms: '30',
    currency: 'SAR'
  });

  const [toggles, setToggles] = useState({
    sendWelcomeEmail: true,
    isVatRegistered: true,
    allowBulkEmails: true,
    allowCreditPayments: false,
  });

  const [termsExpanded, setTermsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [autoCodeEnabled, setAutoCodeEnabled] = useState(false);

  useEffect(() => {
    fetchSettings();
    if (isEditMode) {
      fetchCustomer();
    }
  }, [id]);

  const fetchSettings = async () => {
    try {
      const response = await settingsService.getSettings();
      if (response.success && response.data) {
        const isAutoCode = response.data.general?.customerAutoCode;
        setAutoCodeEnabled(isAutoCode);
        if (isAutoCode && !isEditMode) {
          try {
            const autoCodeResponse = await settingsService.getAutoCode('customer');
            if (autoCodeResponse.success && autoCodeResponse.data) {
              setFormData(prev => ({
                ...prev,
                code: autoCodeResponse.data
              }));
            }
          } catch (codeErr) {
            console.error('Error fetching auto code:', codeErr);
            // Fallback to timestamp based if API fails
            setFormData(prev => ({
              ...prev,
              code: `CUST-${Math.floor(Date.now() / 1000).toString().slice(-6)}`
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const customer = await customerService.getCustomer(id);
      console.log('Fetched customer data:', customer);
      
      setFormData({
        name: customer.name || '',
        customerType: customer.customerType || 'B2B',
        email: customer.email || '',
        phone: customer.phone || '',
        vatNumber: customer.vatRegistrationNumber || '',
        identificationType: customer.identificationType || '',
        identificationNo: customer.identificationNo || '',
        address: {
          street: customer.address?.street || '',
          shortAddress: customer.address?.shortAddress || '',
          city: customer.address?.city || '',
          state: customer.address?.state || '',
          postalCode: customer.address?.postalCode || '',
          country: customer.address?.country || 'SA'
        },
        isActive: customer.isActive !== undefined ? customer.isActive : true,
        code: customer.code || '',
        description: customer.description || '',
        paymentTerms: customer.paymentTerms || '30',
        currency: customer.currency || 'SAR'
      });
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError(err.message || 'Failed to fetch customer');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name.split('.')[1]; // Get the field name after the dot
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [fieldName]: value
      }
    }));
  };

  const handleCustomerTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      customerType: newType,
      vatNumber: newType === 'B2C' ? '' : prev.vatNumber
    }));
  };

  const handleTermsToggle = () => {
    setTermsExpanded(!termsExpanded);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validateForm = () => {
    const errors = {};
    
    // Required fields validation
    if (!formData.code.trim()) {
      errors.code = 'Customer code is required';
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    // Email and Phone validation only for B2C or if provided
    if (formData.customerType === 'B2C') {
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }

      if (!formData.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!/^\+?[0-9\s-]{10,}$/.test(formData.phone)) {
        errors.phone = 'Please enter a valid phone number';
      }
    } else {
      // For B2B, validate format only if provided
      if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (formData.phone.trim() && !/^\+?[0-9\s-]{10,}$/.test(formData.phone)) {
        errors.phone = 'Please enter a valid phone number';
      }
    }

    // Address validation - required for all
    if (!formData.address.street.trim()) {
      errors['address.street'] = 'Street address is required';
    }

    // B2B specific validations
    if (formData.customerType === 'B2B') {
      if (!formData.vatNumber.trim()) {
        errors.vatNumber = 'VAT number is required for B2B customers';
      } else if (!/^[0-9]{15}$/.test(formData.vatNumber)) {
        errors.vatNumber = 'VAT number must be 15 digits';
      }

      if (!formData.identificationType) {
        errors.identificationType = 'Identification type is required for B2B customers';
      }

      if (!formData.identificationNo.trim()) {
        errors.identificationNo = 'Identification number is required for B2B customers';
      }

      if (!formData.address.city.trim()) {
        errors['address.city'] = 'City is required for B2B customers';
      }

      if (!formData.address.state.trim()) {
        errors['address.state'] = 'State/Province is required for B2B customers';
      }

      if (!formData.address.postalCode.trim()) {
        errors['address.postalCode'] = 'Postal code is required for B2B customers';
      }
    }

    setValidationErrors(errors);
    
    // Auto-switch to tab with errors
    if (Object.keys(errors).length > 0 && formData.customerType === 'B2B') {
      // Check if errors are in Business Details tab (tab 1)
      const businessDetailsErrors = ['vatNumber', 'identificationType', 'identificationNo', 'address.city', 'address.state', 'address.postalCode'];
      const hasBusinessDetailsError = businessDetailsErrors.some(field => errors[field]);
      
      if (hasBusinessDetailsError && activeTab === 0) {
        setActiveTab(1);
      }
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedData = {
        name: formData.name,
        code: formData.code.trim(),
        email: formData.email,
        phone: formData.phone,
        customerType: formData.customerType,
        isActive: formData.isActive,
        address: {
          street: formData.address.street,
          shortAddress: formData.address.shortAddress,
          city: formData.address.city,
          state: formData.address.state,
          postalCode: formData.address.postalCode,
          country: formData.address.country
        },
        description: formData.description,
        paymentTerms: formData.paymentTerms,
        currency: formData.currency
      };

      if (formData.customerType === 'B2B' && formData.vatNumber) {
        formattedData.vatRegistrationNumber = formData.vatNumber;
      }

      if (formData.customerType === 'B2B') {
        if (formData.identificationType) {
          formattedData.identificationType = formData.identificationType;
        }
        if (formData.identificationNo) {
          formattedData.identificationNo = formData.identificationNo;
        }
      }

      if (isEditMode) {
        await customerService.updateCustomer(id, formattedData);
      } else {
        await customerService.createCustomer(formattedData);
      }
      navigate('/customers');
    } catch (err) {
      console.error('Error saving customer:', err.response?.data || err);
      setError(err.response?.data?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  // Toggle card component
  const ToggleCard = ({ title, description, name, checked, onChange, infoTooltip }) => (
    <Card variant="outlined" sx={{ mb: 2, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: '10px' }}>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {title}
            </Typography>
            {infoTooltip && (
              <Tooltip title={infoTooltip} arrow>
                <IconButton size="small" sx={{ ml: 0.5 }}>
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Switch
          checked={checked}
          onChange={() => onChange(name)}
          color="primary"
          sx={{ 
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4361ee',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#4361ee',
            }
          }}
        />
      </CardContent>
    </Card>
  );

  // Update the common styling for all textboxes and dropdowns
  const controlStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 36,
      '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: 0,
      },
    },
    '& .MuiInputBase-input': {
      py: 0.75,
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem'
    },
    '& .MuiSelect-select': {
      py: 0.75,
      borderRadius: 0,
    }
  };

  // Update the multiline textbox style
  const multilineStyle = {
    ...controlStyle,
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      minHeight: 36,
      '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: 0,
      },
    }
  };

  // Add specific style for Select components
  const selectStyle = {
    ...controlStyle,
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 36,
      '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: 0,
      },
    },
    '& .MuiSelect-select': {
      py: 0.75,
      borderRadius: 0,
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={handleCancel}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                {isEditMode ? t('CUSTOMERS.Edit_Customer') : t('CUSTOMERS.New_Customer')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                {isEditMode ? t('CUSTOMERS.Update_customer_information') : t('CUSTOMERS.Add_a_new_customer_to_your_account')}
              </Typography>
            </Box>
          </Box>

          {toggles.isVatRegistered && (
            <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
            {t('CUSTOMERS.VAT_REGISTERED_CUSTOMERS_NOTICE')}
            </Alert>
          )}

          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              mb: 0.5, 
              borderRadius: '4px',
              boxShadow: 'none',
              border: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                mb: 0.5,
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 120,
                  py: 0.5,
                  minHeight: 36,
                  fontSize: '0.875rem'
                }
              }}
            >
              <Tab label={t('CUSTOMERS.Basic_Information')} />
              {formData.customerType === 'B2B' && <Tab label={t('CUSTOMERS.Business_Details')} />}
            </Tabs>

            <Grid container spacing={0.5}>
              {/* Basic Information Tab */}
              {activeTab === 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="dense" size="small" sx={selectStyle}>
                    <InputLabel>{t('CUSTOMERS.Customer_Type')}</InputLabel>
                    <Select
                      name="customerType"
                      value={formData.customerType}
                      onChange={handleCustomerTypeChange}
                      label="Customer Type"
                      disabled={isEditMode}
                      autoComplete="off"
                    >
                      <MenuItem value="B2B">Business (B2B)</MenuItem>
                      <MenuItem value="B2C">Individual (B2C)</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label={t('CUSTOMERS.Code')}
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    placeholder="Customer code"
                    sx={controlStyle}
                    autoComplete="off"
                    autoFocus={!autoCodeEnabled}
                    InputProps={{
                      readOnly: autoCodeEnabled || isEditMode,
                    }}
                    error={!!validationErrors.code}
                    helperText={validationErrors.code}
                  />

                  <TextField
                    label={formData.customerType === 'B2B' ? t("CUSTOMERS.Business_Name") : t("CUSTOMERS.Customer_Name")}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    placeholder="Customer Name"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors.name}
                    helperText={validationErrors.name}
                  />

                  <TextField
                    label={t('CUSTOMERS.Address')}
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleAddressChange}
                    fullWidth
                    multiline
                    rows={2}
                    margin="dense"
                    size="small"
                    placeholder={t("Common.Street_Address")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <LocationIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...multilineStyle,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: validationErrors['address.street'] ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover fieldset': {
                          borderColor: validationErrors['address.street'] ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: validationErrors['address.street'] ? 'error.main' : '#4361ee',
                        },
                      },
                    }}
                    autoComplete="off"
                    error={!!validationErrors['address.street']}
                    helperText={validationErrors['address.street']}
                  />

                  <TextField
                    label={t("Common.Short_Address")}
                    name="address.shortAddress"
                    value={formData.address.shortAddress}
                    onChange={handleAddressChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    placeholder="Brief address for invoices"
                    sx={controlStyle}
                    autoComplete="off"
                  />

                  <TextField
                    label={t('CUSTOMERS.Email_Address')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    required={formData.customerType === 'B2C'}
                    margin="dense"
                    size="small"
                    placeholder="contact@example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors.email}
                    helperText={validationErrors.email}
                  />

                  <TextField
                    label={t('CUSTOMERS.Phone_Number')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    fullWidth
                    required={formData.customerType === 'B2C'}
                    margin="dense"
                    size="small"
                    placeholder="+966 5x xxx xxxx"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors.phone}
                    helperText={validationErrors.phone}
                  />

                  <TextField
                    label={t('Common.Description')}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    margin="dense"
                    size="small"
                    placeholder="Enter any additional information"
                    sx={multilineStyle}
                    autoComplete="off"
                  />

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              )}

              {/* Business Details Tab */}
              {activeTab === 1 && formData.customerType === 'B2B' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('Common.VAT_Number')}
                    name="vatNumber"
                    value={formData.vatNumber}
                    onChange={handleChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    required={formData.customerType === 'B2B'}
                    disabled={formData.customerType !== 'B2B'}
                    placeholder="E.g., 310175397400003"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors.vatNumber}
                    helperText={validationErrors.vatNumber}
                  />

                  <FormControl fullWidth margin="dense" size="small" sx={selectStyle} required>
                    <InputLabel>{t('CUSTOMERS.Identification_Type')} *</InputLabel>
                    <Select
                      name="identificationType"
                      value={formData.identificationType}
                      onChange={handleChange}
                      label={`${t('CUSTOMERS.Identification_Type')} *`}
                      autoComplete="off"
                      required
                      error={!!validationErrors.identificationType}
                    >
                      <MenuItem value="CRN">CRN - Commercial Reg No</MenuItem>
                      <MenuItem value="MOM">MOM - MOMRAH License</MenuItem>
                      <MenuItem value="MLS">MLS - MHRSD License</MenuItem>
                      <MenuItem value="SAG">SAG - SAGIA License</MenuItem>
                      <MenuItem value="OTH">OTH - Others</MenuItem>
                      <MenuItem value="700">700 - 700 number</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label={t('CUSTOMERS.Identification_No')}
                    name="identificationNo"
                    value={formData.identificationNo}
                    onChange={handleChange}
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    placeholder="Enter identification number"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors.identificationNo}
                    helperText={validationErrors.identificationNo}
                  />

                  <TextField
                    label={t('Common.Street_Name')}
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleAddressChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    placeholder="Enter street name"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors['address.street']}
                    helperText={validationErrors['address.street']}
                  />

                  <TextField
                    label={t('Common.City_Name')}
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleAddressChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    placeholder="Enter city name"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors['address.city']}
                    helperText={validationErrors['address.city']}
                  />

                  <TextField
                    label={t('Common.State_Province')}
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleAddressChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    placeholder="Enter state/province"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors['address.state']}
                    helperText={validationErrors['address.state']}
                  />

                  <TextField
                    label={t('Common.Postal_Code')}
                    name="address.postalCode"
                    value={formData.address.postalCode}
                    onChange={handleAddressChange}
                    fullWidth
                    required
                    margin="dense"
                    size="small"
                    placeholder="Enter postal code"
                    sx={controlStyle}
                    autoComplete="off"
                    error={!!validationErrors['address.postalCode']}
                    helperText={validationErrors['address.postalCode']}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
            {t('Common.Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<SaveIcon />}
            >
              {loading ? 'Saving...' : isEditMode ? t('Common.Update') : t('Common.Save')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default NewCustomer; 